import { useState, useEffect, useCallback, useRef } from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import moment from "moment";
import { consola } from "consola";
import { useRecoilValue } from "recoil";
import organizationIdState from "../atoms/organizationIdAtom";
import { hasAnsweredDailyOptional } from "../utils/dailyOptionalQuestionsUtils";
import {
  getMonthRange,
  getMonthRangeFromKey,
} from "../utils/monthDateRange";

// Safely convert a Firestore Timestamp or ISO string to a JS Date
const toDate = (value) => {
  if (!value) return null;
  if (typeof value === "string") return new Date(value);
  if (typeof value.toDate === "function") return value.toDate();
  return new Date(value);
};

// Data validation configuration
const dataValidation = {
  nutrition: {
    required: false,
    validWhen: {
      anyPositive: [
        "reportData.carbs",
        "reportData.proteins",
        "reportData.fats",
        "reportData.water",
        "reportData.steps",
        "reportData.weight",
      ],
    },
  },
  exercise: {
    required: false,
    validWhen: {
      hasExercises: "reportData.exercises.length",
    },
  },
  photos: {
    required: false,
    validWhen: {
      hasPhotos: true,
    },
  },
};

const isDataValid = (data, validationRules) => {
  if (!data) return false;
  if (validationRules === true) return true;

  if (validationRules.anyPositive) {
    return validationRules.anyPositive.some((path) => {
      const value = path.split(".").reduce((obj, key) => obj?.[key], data);
      return value > 0;
    });
  }

  if ("hasExercises" in validationRules) {
    const value = validationRules.hasExercises
      .split(".")
      .reduce((obj, key) => obj?.[key], data);
    return value > 0;
  }

  return false;
};

/**
 * Hook to fetch and combine health and exercise reports with month-based paging.
 * Loads the current month on mount; call ensureMonthLoaded(YYYY-MM) when navigating.
 */
const useDailyReports = ({ clientId } = {}) => {
  const organizationId = useRecoilValue(organizationIdState);

  const [healthReports, setHealthReports] = useState({});
  const [exerciseReports, setExerciseReports] = useState({});
  const [loading, setLoading] = useState(true);
  const [monthLoading, setMonthLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(new Date());
  const [dailyReport, setDailyReport] = useState(null);
  const [recentReportSummaries, setRecentReportSummaries] = useState([]);
  const [calendarModifiers, setCalendarModifiers] = useState({});
  const [photosByDate, setPhotosByDate] = useState({});

  const loadedMonthsRef = useRef(new Set());
  const loadingMonthsRef = useRef(new Set());
  const clientIdRef = useRef(clientId);

  const buildPhotoMapFromDocs = useCallback((docs) => {
    const groups = {};
    docs.forEach((doc) => {
      const data = doc.data();
      if (!data.groupId) return;
      if (!(data.groupId in groups)) {
        groups[data.groupId] = {
          photos: {},
          metadata: {
            createdOn: data.createdOn?.toDate?.() || toDate(data.createdOn),
            configuration: data.configuration,
          },
        };
      }
      groups[data.groupId].photos[data.type] = data.url;
    });

    const photoMap = {};
    Object.values(groups).forEach((group) => {
      const createdOn = group.metadata?.createdOn;
      if (!createdOn) return;
      const dateObject =
        createdOn instanceof Date ? createdOn : new Date(createdOn);
      if (isNaN(dateObject)) return;
      const dateString = dateObject.toDateString();
      const photosList = [];
      if (group.photos && Array.isArray(group.metadata?.configuration)) {
        group.metadata.configuration.forEach((config) => {
          const id = config.id;
          const label = config.name || id;
          const url = group.photos[id];
          if (typeof url === "string" && url.trim() !== "") {
            photosList.push({ url, label });
          }
        });
      } else if (group.photos) {
        Object.entries(group.photos).forEach(([id, url]) => {
          if (typeof url === "string" && url.trim() !== "") {
            photosList.push({ url, label: id });
          }
        });
      }
      photoMap[dateString] = {
        hasPhoto: photosList.length > 0,
        photos: photosList,
      };
    });
    return photoMap;
  }, []);

  const fetchMonthData = useCallback(
    async (monthKey) => {
      if (!clientId || !organizationId || !monthKey) return null;

      const { start, end } = getMonthRangeFromKey(monthKey);

      const photosRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("progressPhotos")
        .where("clientId", "==", clientId)
        .where("createdOn", ">=", start)
        .where("createdOn", "<=", end)
        .orderBy("createdOn", "desc");

      consola.debug("++++++ READ -> useDailyReports: fetchMonth photos", monthKey);
      const photosSnap = await photosRef.get();
      const photoMap = buildPhotoMapFromDocs(photosSnap.docs);

      const healthUpdates = {};
      const exerciseUpdates = {};

      const healthReportRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("clients")
        .doc(clientId)
        .collection("reportHealth")
        .where("createdOn", ">=", start)
        .where("createdOn", "<=", end)
        .orderBy("createdOn", "desc");

      consola.debug("++++++ READ -> useDailyReports: fetchMonth health", monthKey);
      const healthData = await healthReportRef.get();
      healthData.docs.forEach((doc) => {
        const data = doc.data();
        const date = toDate(data.createdOn);
        if (!date) return;
        healthUpdates[date.toDateString()] = data;
      });

      const broadStart = moment(start).subtract(14, "days").toDate();
      const broadEnd = moment(end).add(14, "days").toDate();
      const exerciseReportRef = firebase
        .firestore()
        .collection("organizations")
        .doc(organizationId)
        .collection("clients")
        .doc(clientId)
        .collection("reportExercise")
        .where("createdOn", ">=", broadStart)
        .where("createdOn", "<=", broadEnd)
        .orderBy("createdOn", "desc");

      consola.debug(
        "++++++ READ -> useDailyReports: fetchMonth exercise",
        monthKey,
      );
      const exerciseData = await exerciseReportRef.get();
      exerciseData.docs.forEach((doc) => {
        const data = doc.data();
        let dateToUse;
        if (data.completedOn && data.status === "complete") {
          dateToUse = toDate(data.completedOn);
        } else {
          dateToUse = toDate(data.createdOn);
        }
        if (!dateToUse) return;
        if (dateToUse >= start && dateToUse <= end) {
          exerciseUpdates[dateToUse.toDateString()] = data;
        }
      });

      return { photoMap, healthUpdates, exerciseUpdates };
    },
    [clientId, organizationId, buildPhotoMapFromDocs],
  );

  const ensureMonthLoaded = useCallback(
    async (monthKey) => {
      if (!clientId || !organizationId || !monthKey) return;
      if (loadedMonthsRef.current.has(monthKey)) return;
      if (loadingMonthsRef.current.has(monthKey)) return;

      loadingMonthsRef.current.add(monthKey);
      setMonthLoading(true);
      setError(null);

      try {
        const result = await fetchMonthData(monthKey);
        if (!result || clientIdRef.current !== clientId) return;

        loadedMonthsRef.current.add(monthKey);
        setPhotosByDate((prev) => ({ ...prev, ...result.photoMap }));
        setHealthReports((prev) => ({ ...prev, ...result.healthUpdates }));
        setExerciseReports((prev) => ({ ...prev, ...result.exerciseUpdates }));
      } catch (err) {
        consola.error("Error fetching month reports:", err);
        setError(err);
      } finally {
        loadingMonthsRef.current.delete(monthKey);
        setMonthLoading(loadingMonthsRef.current.size > 0);
      }
    },
    [clientId, organizationId, fetchMonthData],
  );

  // Initial load: current month (reset when client changes)
  useEffect(() => {
    clientIdRef.current = clientId;
    loadedMonthsRef.current = new Set();
    loadingMonthsRef.current = new Set();
    setHealthReports({});
    setExerciseReports({});
    setPhotosByDate({});
    setCalendarModifiers({});
    setRecentReportSummaries([]);
    setDailyReport(null);

    if (!clientId || !organizationId) {
      setLoading(false);
      return undefined;
    }

    let mounted = true;
    const initialKey = getMonthRange(new Date()).key;

    const run = async () => {
      setLoading(true);
      setError(null);
      loadingMonthsRef.current.add(initialKey);
      try {
        const result = await fetchMonthData(initialKey);
        if (!mounted || clientIdRef.current !== clientId || !result) return;
        loadedMonthsRef.current.add(initialKey);
        setPhotosByDate(result.photoMap);
        setHealthReports(result.healthUpdates);
        setExerciseReports(result.exerciseUpdates);
      } catch (err) {
        if (mounted) {
          consola.error("Error fetching initial month reports:", err);
          setError(err);
        }
      } finally {
        loadingMonthsRef.current.delete(initialKey);
        if (mounted) setLoading(false);
      }
    };

    run();
    return () => {
      mounted = false;
    };
  }, [clientId, organizationId, fetchMonthData]);
  const handleDayClick = useCallback(
    (date) => {
      setSelectedDay(date);
      const dateString = date.toDateString();
      const healthData = healthReports[dateString] || null;
      const exerciseData = exerciseReports[dateString] || null;
      const hasPhotos = photosByDate[dateString]?.hasPhoto || false;
      const photos = photosByDate[dateString]?.photos || [];
      const activities = healthData?.reportData?.activities || [];
      const hasQuestions = hasAnsweredDailyOptional(healthData?.reportData);

      setDailyReport({
        date,
        health: healthData,
        exercise: exerciseData,
        weight: healthData?.reportData?.weight || null,
        hasExercise: isDataValid(
          exerciseData,
          dataValidation.exercise.validWhen,
        ),
        hasNutrition: isDataValid(
          healthData,
          dataValidation.nutrition.validWhen,
        ),
        hasActivities: activities.length > 0,
        activities,
        hasQuestions,
        hasPhoto: hasPhotos,
        photos,
      });
    },
    [healthReports, exerciseReports, photosByDate],
  );

  const generateRecentReportSummaries = useCallback(() => {
    const allDates = new Set([
      ...Object.keys(healthReports),
      ...Object.keys(exerciseReports),
    ]);

    const sortedDates = Array.from(allDates).sort(
      (a, b) => new Date(b) - new Date(a),
    );
    const recentDates = sortedDates.slice(0, 10);
    const summaries = [];

    for (const dateString of recentDates) {
      const date = new Date(dateString);
      const healthData = healthReports[dateString];
      const exerciseData = exerciseReports[dateString];
      const hasPhoto = photosByDate[dateString]?.hasPhoto || false;
      const photos = photosByDate[dateString]?.photos || [];

      summaries.push({
        date,
        hasExercise: exerciseData !== null,
        hasNutrition:
          healthData !== null &&
          (healthData?.reportData?.carbs > 0 ||
            healthData?.reportData?.proteins > 0 ||
            healthData?.reportData?.fats > 0),
        hasQuestions: hasAnsweredDailyOptional(healthData?.reportData),
        hasPhoto,
        photos,
        weight: healthData?.reportData?.weight || null,
        nutrition: healthData
          ? {
              proteins: healthData.reportData?.proteins || 0,
              carbs: healthData.reportData?.carbs || 0,
              fats: healthData.reportData?.fats || 0,
              water: healthData.reportData?.water || 0,
              steps: healthData.reportData?.steps || 0,
            }
          : null,
        activities: healthData?.reportData?.activities || [],
        hasActivities:
          Array.isArray(healthData?.reportData?.activities) &&
          healthData.reportData.activities.length > 0,
        exercise: exerciseData
          ? {
              totalExercises: exerciseData.reportData?.exercises?.length || 0,
              completedExercises:
                exerciseData.reportData?.exercises?.filter((ex) => ex.completed)
                  ?.length || 0,
              status: exerciseData.status || null,
              protocolName: exerciseData.reportData?.protocol?.name || null,
              splitName: exerciseData.reportData?.split?.name || null,
            }
          : null,
      });
    }

    setRecentReportSummaries(summaries);
  }, [healthReports, exerciseReports, photosByDate]);

  useEffect(() => {
    if (
      Object.keys(healthReports).length > 0 ||
      Object.keys(exerciseReports).length > 0
    ) {
      generateRecentReportSummaries();
    }
  }, [healthReports, exerciseReports, generateRecentReportSummaries]);

  const updateCalendarModifiers = useCallback(() => {
    const allDates = new Set([
      ...Object.keys(healthReports),
      ...Object.keys(exerciseReports),
      ...Object.keys(photosByDate),
    ]);

    const newModifiers = {};
    allDates.forEach((dateString) => {
      const healthData = healthReports[dateString];
      const exerciseData = exerciseReports[dateString];
      const hasNutritionData = isDataValid(
        healthData,
        dataValidation.nutrition.validWhen,
      );
      const hasExerciseData = isDataValid(
        exerciseData,
        dataValidation.exercise.validWhen,
      );
      const hasPhotos = photosByDate[dateString]?.hasPhoto || false;
      const hasActivitiesData =
        Array.isArray(healthData?.reportData?.activities) &&
        healthData.reportData.activities.length > 0;
      const hasQuestionsData = hasAnsweredDailyOptional(healthData?.reportData);

      if (
        hasNutritionData ||
        hasExerciseData ||
        hasPhotos ||
        hasActivitiesData ||
        hasQuestionsData
      ) {
        newModifiers[dateString] = {
          hasNutrition: hasNutritionData,
          hasExercise: hasExerciseData,
          hasPhotos,
          hasActivities: hasActivitiesData,
          hasQuestions: hasQuestionsData,
          exerciseStatus: exerciseData?.status || null,
        };
      }
    });

    setCalendarModifiers((prev) => {
      const prevKeys = Object.keys(prev);
      const newKeys = Object.keys(newModifiers);
      if (prevKeys.length !== newKeys.length) return newModifiers;
      const hasChanges = newKeys.some((key) => {
        const prevMod = prev[key];
        const newMod = newModifiers[key];
        return (
          !prevMod ||
          prevMod.hasNutrition !== newMod.hasNutrition ||
          prevMod.hasExercise !== newMod.hasExercise ||
          prevMod.hasPhotos !== newMod.hasPhotos ||
          prevMod.hasActivities !== newMod.hasActivities ||
          prevMod.hasQuestions !== newMod.hasQuestions ||
          prevMod.exerciseStatus !== newMod.exerciseStatus
        );
      });
      return hasChanges ? newModifiers : prev;
    });
  }, [healthReports, exerciseReports, photosByDate]);

  useEffect(() => {
    updateCalendarModifiers();
  }, [updateCalendarModifiers]);

  const getReportForDate = useCallback(
    async (date) => {
      if (!clientId || !organizationId) return null;

      const dateIndex = moment(date).format("YYYY-MM-DD");

      try {
        const healthReportRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("clients")
          .doc(clientId)
          .collection("reportHealth")
          .doc(dateIndex);

        consola.debug("++++++ READ -> useDailyReports: getReportForDate health");
        const healthDoc = await healthReportRef.get();
        const healthData = healthDoc.exists ? healthDoc.data() : null;

        const dayStart = moment(date).startOf("day").toDate();
        const dayEnd = moment(date).endOf("day").toDate();

        const completedExerciseRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("clients")
          .doc(clientId)
          .collection("reportExercise")
          .where("completedOn", ">=", dayStart)
          .where("completedOn", "<=", dayEnd)
          .where("status", "==", "complete")
          .orderBy("completedOn", "desc");

        const createdExerciseRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId)
          .collection("clients")
          .doc(clientId)
          .collection("reportExercise")
          .where("createdOn", ">=", dayStart)
          .where("createdOn", "<=", dayEnd)
          .orderBy("createdOn", "desc");

        consola.debug(
          "++++++ READ -> useDailyReports: getReportForDate exercise",
        );

        const [completedDocs, createdDocs] = await Promise.all([
          completedExerciseRef.get(),
          createdExerciseRef.get(),
        ]);

        let exerciseData = null;
        if (completedDocs.docs.length > 0) {
          exerciseData = completedDocs.docs[0].data();
        } else if (createdDocs.docs.length > 0) {
          const createdWorkout = createdDocs.docs[0].data();
          if (createdWorkout.status !== "complete") {
            exerciseData = createdWorkout;
          }
        }

        if (!exerciseData) {
          const dateIndexQuery = firebase
            .firestore()
            .collection("organizations")
            .doc(organizationId)
            .collection("clients")
            .doc(clientId)
            .collection("reportExercise")
            .where("dateIndex", "==", moment(date).format("YYYY-MM-DD"))
            .limit(1);

          consola.debug(
            "++++++ READ -> useDailyReports: getReportForDate dateIndex fallback",
          );
          const dateIndexDocs = await dateIndexQuery.get();
          if (dateIndexDocs.docs.length > 0) {
            exerciseData = dateIndexDocs.docs[0].data();
          }
        }

        const activities = healthData?.reportData?.activities || [];
        return {
          date,
          health: healthData,
          exercise: exerciseData,
          weight: healthData?.reportData?.weight || null,
          hasExercise: exerciseData !== null,
          exerciseStatus: exerciseData?.status || null,
          hasNutrition:
            healthData !== null &&
            (healthData.reportData.carbs > 0 ||
              healthData.reportData.proteins > 0 ||
              healthData.reportData.fats > 0),
          hasActivities: activities.length > 0,
          activities,
          hasQuestions: hasAnsweredDailyOptional(healthData?.reportData),
          hasPhoto: photosByDate[date.toDateString()]?.hasPhoto || false,
          photos: photosByDate[date.toDateString()]?.photos || [],
        };
      } catch (err) {
        consola.error("Error fetching report for date:", err);
        return null;
      }
    },
    [clientId, organizationId, photosByDate],
  );

  const refreshSelectedDayReport = useCallback(async () => {
    if (!selectedDay) return;
    const newReport = await getReportForDate(selectedDay);
    setDailyReport(newReport);
  }, [selectedDay, getReportForDate]);

  useEffect(() => {
    if (
      selectedDay &&
      !loading &&
      (Object.keys(healthReports).length > 0 ||
        Object.keys(exerciseReports).length > 0 ||
        Object.keys(photosByDate).length > 0)
    ) {
      handleDayClick(selectedDay);
    }
  }, [
    selectedDay,
    loading,
    healthReports,
    exerciseReports,
    photosByDate,
    handleDayClick,
  ]);

  return {
    healthReports,
    exerciseReports,
    loading,
    monthLoading,
    error,
    calendarModifiers,
    selectedDay,
    dailyReport,
    recentReportSummaries,
    handleDayClick,
    getReportForDate,
    refreshSelectedDayReport,
    ensureMonthLoaded,
  };
};

export default useDailyReports;
